import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { sortByCat } from "@/lib/posterTemplate";
import { textExtent } from "@/components/UscLayout";
import { toast } from "sonner";
import { ArrowDownAZ, X, Move, AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignVerticalSpaceAround } from "lucide-react";

const lineEl = (id) => [...document.querySelectorAll(`[data-testid="poster-editor-canvas"] [data-testid="simple-line-${id}"]`)].find((e) => !e.closest("[aria-hidden]"));
const scale = () => {
  const lay = [...document.querySelectorAll('[data-testid="poster-editor-canvas"] [data-testid="usc-layout"]')][0];
  return lay ? lay.getBoundingClientRect().width / 1280 : 1;
};
const ALIGNS = [["left", AlignStartVertical, "Aligner à gauche"], ["center", AlignCenterVertical, "Centrer"], ["right", AlignEndVertical, "Aligner à droite"], ["space", AlignVerticalSpaceAround, "Espacer régulièrement (vertical)"]];
const alignShifts = (sel, how) => {
  const k = scale();
  const items = sel.map((id) => ({ id, el: lineEl(id) })).filter((x) => x.el).map((x) => ({ id: x.id, ...textExtent(x.el) }));
  if (items.length < 2) return null;
  const out = {};
  if (how === "space") {
    const s = [...items].sort((a, b) => a.t - b.t);
    const step = (s[s.length - 1].t - s[0].t) / (s.length - 1);
    s.forEach((x, i) => { out[x.id] = { dy: (s[0].t + i * step - x.t) / k }; });
    return out;
  }
  const L = Math.min(...items.map((x) => x.l)), R = Math.max(...items.map((x) => x.r));
  items.forEach((x) => {
    const target = how === "left" ? L : how === "right" ? R - (x.r - x.l) : (L + R) / 2 - (x.r - x.l) / 2;
    out[x.id] = { dx: (target - x.l) / k };
  });
  return out;
};

export const mapBlocks = (d, fn) => ({
  ...d,
  top: d.top ? { ...d.top, rows: fn(d.top.rows || []) } : d.top,
  columns: (d.columns || []).map((c) => ({ ...c, rows: fn(c.rows || []) })),
  footer: fn(d.footer || []),
});
const rowsOf = (d) => [...(d.top?.rows || []), ...(d.columns || []).flatMap((c) => c.rows || []), ...(d.footer || [])];

const sortSelected = (sel) => (rows) => {
  const slots = rows.map((r, i) => (sel.includes(r.id) ? i : -1)).filter((i) => i >= 0);
  const sorted = sortByCat(slots.map((i) => rows[i]));
  const out = [...rows];
  slots.forEach((i, n) => { out[i] = { ...sorted[n], dx: rows[i].dx, dy: rows[i].dy }; });
  return out;
};

export const SelectionPanel = ({ data, setData, fonts }) => {
  const sel = data._sel || [];
  if (!sel.length) return null;
  const first = rowsOf(data).find((r) => sel.includes(r.id)) || {};
  const style = (patch) => setData(mapBlocks(data, (rows) => rows.map((r) => (sel.includes(r.id) ? { ...r, s: { ...(r.s || {}), ...patch } } : r))));
  return (
    <div className="space-y-3 rounded-lg border border-sky-500/50 bg-sky-500/5 p-3" data-testid="selection-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold" data-testid="selection-count" title="Flèches du clavier : déplacer de 1 px (Maj : 10 px)">{sel.length} ligne{sel.length > 1 ? "s" : ""} sélectionnée{sel.length > 1 ? "s" : ""}</p>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setData({ ...data, _sel: [] })} data-testid="clear-selection-button">
          <X className="mr-1 h-3.5 w-3.5" /> Désélectionner
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground" data-testid="arrow-hint">Flèches du clavier : déplacer de 1 px · Maj + flèche : 10 px</p>
      <div className="flex items-center gap-2">
        <Label className="w-14 shrink-0 text-xs">Taille</Label>
        <Slider value={[first.s?.fs || 100]} min={50} max={200} step={5} onValueChange={([fs]) => style({ fs })} className="flex-1" data-testid="line-size-slider" />
        <span className="w-10 text-right font-mono text-[10px] text-muted-foreground">{first.s?.fs || 100}%</span>
      </div>
      <div className="flex items-center gap-2">
        <Label className="w-14 shrink-0 text-xs">Police</Label>
        <select value={first.s?.font || ""} onChange={(e) => style({ font: e.target.value })} data-testid="line-font-select"
          className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs">
          <option value="">Comme le reste</option>
          {fonts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <Label className="w-14 shrink-0 text-xs">Aligner</Label>
        {ALIGNS.map(([how, Icon, title]) => (
          <Button key={how} size="icon" variant="secondary" className="h-8 w-8" title={title} disabled={sel.length < 2} data-testid={`align-selected-${how}`}
            onClick={() => {
              const sh = alignShifts(sel, how);
              if (!sh) return toast.error("Sélectionnez au moins 2 lignes visibles");
              setData(mapBlocks(data, (rows) => rows.map((r) => (sh[r.id] ? { ...r, dx: Math.round((r.dx || 0) + (sh[r.id].dx || 0)), dy: Math.round((r.dy || 0) + (sh[r.id].dy || 0)) } : r))));
            }}>
            <Icon className="h-4 w-4" />
          </Button>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" className="h-8 flex-1 text-xs" data-testid="sort-selected-button"
          onClick={() => { setData(mapBlocks(data, sortSelected(sel))); toast.success("Lignes sélectionnées triées par catégorie"); }}>
          <ArrowDownAZ className="mr-1 h-3.5 w-3.5" /> Trier par catégorie
        </Button>
        <Button size="sm" variant="secondary" className="h-8 flex-1 text-xs" data-testid="reset-selected-position-button"
          onClick={() => setData(mapBlocks(data, (rows) => rows.map((r) => (sel.includes(r.id) ? { ...r, dx: 0, dy: 0 } : r))))}>
          <Move className="mr-1 h-3.5 w-3.5" /> Remettre en place
        </Button>
      </div>
    </div>
  );
};

export const resetAllPositions = (d) => {
  const clear = (o) => ({ ...o, dx: 0, dy: 0 });
  const m = mapBlocks(d, (rows) => rows.map(clear));
  return { ...m, top: m.top ? clear(m.top) : m.top, columns: m.columns.map(clear), band: { ...(d.band || {}), moves: {} } };
};
export const hasMoves = (d) => Object.keys(d.band?.moves || {}).length > 0
  || [d.top || {}, ...(d.columns || []), ...rowsOf(d)].some((o) => o.dx || o.dy);
